import { useEffect, useState } from "react";
import api, { moneyExact } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Modal } from "../admin/Projects";
import { CheckCircle2, FileSignature, FileText } from "lucide-react";

export default function PortalEstimates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/estimates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await api.post(`/estimates/${id}/approve`);
    load();
    setOpen(null);
  };

  const openPdf = async (eid) => {
    try {
      const res = await api.get(`/estimates/${eid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      alert("Could not open PDF: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <DashboardLayout role="client" title="Your Estimates" subtitle="Review and approve estimates from Coastal Bend Contractor Co.">
      {items.length === 0 ? (
        <div className="card p-12 text-center text-sm" style={{color:'var(--text-muted)'}}>
          <FileSignature className="mx-auto mb-3"/>No estimates yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((e) => (
            <button key={e.id} onClick={() => setOpen(e)} className="card p-5 text-left hover:translate-y-[-2px] transition-transform" data-testid={`portal-est-${e.id}`}>
              <div className="flex items-baseline justify-between mb-1">
                <div className="font-display text-lg font-bold">{e.title}</div>
                <span className={`badge ${e.status === 'approved' ? 'badge-green' : ''}`}>{e.status}</span>
              </div>
              <div className="text-xs mb-4" style={{color:'var(--text-muted)'}}>Created {new Date(e.created_at).toLocaleDateString()}</div>
              <div className="text-xl font-display font-bold" style={{color:'var(--accent)'}}>{moneyExact(e.total)}</div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <Modal onClose={() => setOpen(null)} title={open.title}>
          <div className="text-sm space-y-3 mb-5">
            <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Status</span><span className={`badge ${open.status === 'approved' ? 'badge-green' : ''}`}>{open.status}</span></div>
            <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Created</span><span>{new Date(open.created_at).toLocaleDateString()}</span></div>
          </div>
          <div className="border-t border-b border-white/5 py-4 my-4">
            <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider mb-2" style={{color:'var(--text-muted)'}}>
              <div className="col-span-5">Item</div>
              <div className="col-span-2">Qty</div>
              <div className="col-span-2 text-right">Unit Price</div>
              <div className="col-span-3 text-right">Amount</div>
            </div>
            {open.line_items.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 py-2 text-sm border-t border-white/5">
                <div className="col-span-5">
                  <div>{li.description}</div>
                  {(li.item_type || li.color) && (
                    <div className="text-[10px] uppercase tracking-wider mt-0.5 flex gap-2" style={{color:'var(--text-muted)'}}>
                      {li.item_type && <span>{li.item_type.replace('_', ' & ')}</span>}
                      {li.color && <span>· {li.color}</span>}
                    </div>
                  )}
                </div>
                <div className="col-span-2">{li.quantity}{li.unit ? ` ${li.unit}` : ''}</div>
                <div className="col-span-2 text-right">{moneyExact(li.unit_price)}</div>
                <div className="col-span-3 text-right font-semibold">{moneyExact(li.amount)}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Subtotal</span><span>{moneyExact(open.subtotal)}</span></div>
            <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Tax</span><span>{moneyExact(open.tax)}</span></div>
            <div className="flex justify-between font-display text-xl font-bold pt-3 border-t border-white/5"><span>Total</span><span style={{color:'var(--accent)'}}>{moneyExact(open.total)}</span></div>
          </div>
          {open.notes && (
            <div className="mt-5 text-sm">
              <div className="label-overline mb-1.5">Notes</div>
              <p style={{color:'var(--text-muted)'}}>{open.notes}</p>
            </div>
          )}
          <div className="flex gap-3 justify-end mt-6">
            <button className="btn-ghost" onClick={() => setOpen(null)}>Close</button>
            <button className="btn-ghost" onClick={() => openPdf(open.id)} data-testid="portal-est-pdf">
              <FileText size={15}/> View PDF
            </button>
            {open.status !== "approved" && (
              <button className="btn-primary" onClick={() => approve(open.id)} data-testid="approve-est"><CheckCircle2 size={15}/> Approve Estimate</button>
            )}
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
